import { createNextState } from '@reduxjs/toolkit';
import type { UnitInterface } from 'store/units/types';
import type { UnitInfoDataInterface } from 'store/unitInfoData';
import type { UnitTypes } from 'unitData/types';
import {
  getUnitOptionBindings,
  mobileCatalogTypes,
  readField,
} from './unitOptionBindings';
import { resolveOption } from './optionResolution';

const writeField = (
  source: unknown,
  path: readonly string[],
  value: unknown,
) => {
  let target = source as Record<string, unknown>;
  for (const key of path.slice(0, -1)) {
    const next = target[key];
    if (!next || typeof next !== 'object') return;
    target = next as Record<string, unknown>;
  }
  const key = path.at(-1);
  if (key) target[key] = value;
};

/** Display-only copy. Never write resolved labels or missing-option markers to Redux. */
export const resolveUnitLabels = (
  unit: UnitInterface,
  data: UnitInfoDataInterface,
  screenCalcMethod = 'NORMAL',
): UnitInterface =>
  createNextState(unit, (draft) => {
    for (const binding of getUnitOptionBindings(unit, data, screenCalcMethod)) {
      const saved = readField(unit.unitInfo, binding.keyPath ?? binding.path);
      if (
        binding.mode === 'scalar' &&
        (typeof saved === 'string' || typeof saved === 'number')
      ) {
        const savedLabel = binding.keyPath
          ? readField(unit.unitInfo, binding.path)
          : undefined;
        writeField(
          draft.unitInfo,
          binding.path,
          resolveOption(
            binding.options,
            saved,
            typeof savedLabel === 'string' ? savedLabel : undefined,
          ).label,
        );
        continue;
      }
      if (!saved || typeof saved !== 'object') continue;
      const record = saved as Record<string, unknown>;
      const key =
        record.configKey ??
        record.simulationModel ??
        record.value ??
        record.model;
      if (typeof key !== 'string' && typeof key !== 'number') continue;
      const labelField = 'displayName' in record ? 'displayName' : 'name';
      const label =
        typeof record[labelField] === 'string' ? record[labelField] : undefined;
      const resolved = resolveOption(binding.options, key, label);
      const target = readField(draft.unitInfo, binding.path) as Record<
        string,
        unknown
      >;
      if (binding.mode === 'object') {
        if (resolved.option) {
          Object.keys(target).forEach((field) => delete target[field]);
          Object.assign(target, resolved.option);
        }
      } else {
        target[labelField] = resolved.label;
      }
    }
  });

export const resolveUnitModelLabel = (
  type: UnitTypes,
  key: string,
  data: UnitInfoDataInterface,
): string => {
  const catalogType = mobileCatalogTypes[type];
  if (!catalogType) return key;
  const models = data[catalogType];
  const label = readField(models, [key, 'configurations', 'displayName']);
  const options =
    typeof label === 'string' ? [{ value: key, name: label }] : [];
  return resolveOption(options, key).label;
};
